// Checkbox Model - Valtio-based reactive state management for single checkbox
import {proxy, ref} from "valtio";

import {IQueryParamProvider} from "@/shared/contracts";
import {ValidateInterface} from "@/shared/contracts/validate.interface";
import {EventEmitter} from "@/shared/event-emitter";

import {FormFieldEvents, ValidationError} from "./types";

interface CheckboxConfig {
    initialValue?: boolean;
    disabled?: boolean;
    isRequired?: boolean;
    requireMessage?: string;
}

class CheckboxClass implements ValidateInterface, IQueryParamProvider {
    // Reactive state properties
    _checked: boolean;
    _disabled: boolean;
    _errors: Array<ValidationError>;

    // Non-reactive properties
    private readonly initialChecked: boolean;
    private readonly isRequiredValue: boolean;
    private readonly requireMessage: string;
    public readonly events: EventEmitter<FormFieldEvents>;

    constructor(config: CheckboxConfig = {}) {
        this.initialChecked = config.initialValue ?? false;
        this._checked = this.initialChecked;
        this._disabled = config.disabled ?? false;
        this._errors = [];
        this.isRequiredValue = config.isRequired ?? false;
        this.requireMessage = config.requireMessage || "required";
        this.events = ref(new EventEmitter<FormFieldEvents>()) as EventEmitter<FormFieldEvents>;
    }

    // Getters
    get checked(): boolean {
        return this._checked;
    }

    get disabled(): boolean {
        return this._disabled;
    }

    get error(): ValidationError | null {
        return this._errors[0] || null;
    }

    get errors(): Array<ValidationError> {
        return this._errors;
    }

    get hasError(): boolean {
        return this._errors.length > 0;
    }

    get shouldShowError(): boolean {
        return this.hasError;
    }

    get isRequired(): boolean {
        return this.isRequiredValue;
    }

    get isValid(): boolean {
        return this.validate().isValid;
    }

    // Setters
    setDisabled(disabled: boolean): void {
        this._disabled = disabled;
    }

    // Set checked
    setChecked(checked: boolean): void {
        if (this._disabled) return;

        this._checked = checked;
        this._errors = [];
        this.validate();
        this.events.emit("change", String(checked));
    }

    // Toggle checked
    toggle(): void {
        this.setChecked(!this._checked);
    }

    // Validate
    validate(): {isValid: boolean; error?: string} {
        // Simple validation: if required, must be checked
        if (this.isRequiredValue && !this._checked) {
            const error: ValidationError = {
                type: "error",
                message: this.requireMessage,
            };
            this._errors = [error];
            return {isValid: false, error: this.requireMessage};
        }

        this._errors = [];
        return {isValid: true};
    }

    // Reset to initial value
    reset(): void {
        this._checked = this.initialChecked;
        this._errors = [];
        this.events.emit("change", String(this._checked));
    }

    // Clear (uncheck)
    clear(): void {
        this._checked = false;
        this._errors = [];
    }

    // Event handlers
    onBlur(): void {
        this.validate();
        this.events.emit("blur");
    }

    onFocus(): void {
        this.events.emit("focus");
    }

    scrollIntoView(): void {
        this.events.emit("scrollIntoView");
    }

    // Query params (IQueryParamProvider implementation)
    getQueryParam(paramName?: string): string {
        if (!this._checked) {
            return "";
        }

        const name = paramName || "checked";
        return `${name}=true`;
    }

    // Set from query params
    setFromQueryParams(params: URLSearchParams, paramName?: string): void {
        const name = paramName || "checked";
        const value = params.get(name);

        if (value !== null) {
            this._checked = value === "true" || value === "1";
        }
    }
}

// Export type
export type CheckboxInstance = CheckboxClass;

// Factory function
export function Checkbox(config: CheckboxConfig = {}): CheckboxInstance {
    return proxy<CheckboxInstance>(new CheckboxClass(config));
}
